import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import StudioRowActions from "../../common/Components/GeoList/StudioRowActions";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  createKarkhanaDesign,
  deleteKarkhanaDesign,
  listKarkhanaDesigns,
  updateKarkhanaDesign,
} from "../../services/karkhanaApi";
import { geoRecordId } from "../gi-registry/geoPath";
import "../gi-registry/gi-registry.scss";
import PageHeader, { HeaderButton } from "./ui/PageHeader";
import SearchField from "./ui/SearchField";
import { confirmDialog } from "./ui/dialog";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const emptyForm = () => ({
  designNumber: "",
  designName: "",
  designFamily: "",
  technique: "",
  notes: "",
});

const KarkhanaDesignsPage = () => {
  const history = useHistory();
  const role = readAuthUser()?.users?.role;
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState("");
  // The search stays in the address (?q=…, replaced while typing): Back and reload keep it.
  const [search, setSearch] = useAddressParam("q");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (q = "") => {
    setLoading(true);
    setError("");
    try {
      const res: any = await listKarkhanaDesigns({ offset: 0, limit: 200, q: q || undefined });
      if (!res?.success) {
        setRows([]);
        setError(res?.message || "Unable to load designs");
        return;
      }
      setRows(Array.isArray(res?.data?.docs) ? res.data.docs : []);
    } catch {
      setRows([]);
      setError("Unable to load designs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load(new URLSearchParams(history.location.search).get("q") || "");
  }, [role, history, load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setForm({
      designNumber: row.designNumber || "",
      designName: row.designName || "",
      designFamily: row.designFamily || "",
      technique: row.technique || "",
      notes: row.notes || "",
    });
    setOpen(true);
  };

  const persist = async () => {
    if (!form.designNumber.trim() || !form.designName.trim()) {
      notifications({ title: "Error", message: "Design number and name are required", type: "danger" });
      return;
    }
    setSaving(true);
    try {
      const body = {
        designNumber: form.designNumber.trim(),
        designName: form.designName.trim(),
        designFamily: form.designFamily.trim() || undefined,
        technique: form.technique.trim() || undefined,
        notes: form.notes.trim() || undefined,
      };
      const res: any = editing
        ? await updateKarkhanaDesign(geoRecordId(editing), body)
        : await createKarkhanaDesign(body);
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not save design", type: "danger" });
        return;
      }
      setOpen(false);
      await load(search);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: any) => {
    const id = geoRecordId(row);
    if (!id || !(await confirmDialog({ title: `Remove design ${row.designNumber}?` }))) {
      return;
    }
    const res: any = await deleteKarkhanaDesign(id);
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Could not delete", type: "danger" });
      return;
    }
    await load(search);
  };

  if (!canManageKarkhana(role)) {
    return null;
  }

  return (
    <div className="gi-registry-page">
      <PageHeader title="Designs" back={kp.settings()}>
        {canWrite ? (
          <HeaderButton primary add onClick={openCreate}>
            Add design
          </HeaderButton>
        ) : null}
      </PageHeader>
      <SearchField
        value={search}
        onChange={setSearch}
        onSubmit={(v) => load(v)}
        placeholder="Search design number or name"
        label="Search designs"
        count={loading ? "Loading…" : `${rows.length} design${rows.length === 1 ? "" : "s"}`}
      />
      <GeoResponsiveList
        rows={rows}
        rowKey={(ele) => geoRecordId(ele) || ele.designNumber}
        empty={loading ? "Loading designs…" : error || "No designs yet"}
        actions={(ele) =>
          canWrite ? (
            <StudioRowActions
              actions={[
                { key: "edit", label: "Edit", icon: "edit", tone: "primary", onClick: () => openEdit(ele) },
                { key: "delete", label: "Delete", icon: "delete", tone: "danger", onClick: () => remove(ele) },
              ]}
            />
          ) : null
        }
        columns={[
          { key: "number", header: "Number", cell: (row) => row.designNumber },
          { key: "name", header: "Name", cell: (row) => row.designName },
          { key: "family", header: "Family", hideSm: true, cell: (row) => row.designFamily || "—" },
          {
            key: "active",
            header: "Active",
            cell: (row) => (row.active ? "On" : "Off"),
          },
        ]}
      />
      <Modal isOpen={open} toggle={() => setOpen(false)}>
        <ModalHeader toggle={() => setOpen(false)}>{editing ? "Edit design" : "Add design"}</ModalHeader>
        <ModalBody>
          <Label for="kd-number">Design number</Label>
          <Input
            id="kd-number"
            value={form.designNumber}
            onChange={(e) => setForm({ ...form, designNumber: e.target.value })}
          />
          <Label className="mt-2" for="kd-name">Name</Label>
          <Input
            id="kd-name"
            value={form.designName}
            onChange={(e) => setForm({ ...form, designName: e.target.value })}
          />
          <Label className="mt-2" for="kd-family">Family (optional)</Label>
          <Input
            id="kd-family"
            value={form.designFamily}
            onChange={(e) => setForm({ ...form, designFamily: e.target.value })}
          />
          <Label className="mt-2" for="kd-technique">Technique (optional)</Label>
          <Input
            id="kd-technique"
            value={form.technique}
            onChange={(e) => setForm({ ...form, technique: e.target.value })}
          />
          <Label className="mt-2" for="kd-notes">Notes</Label>
          <Input
            id="kd-notes"
            type="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={saving} onClick={persist}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaDesignsPage;
